import { useTranslate } from '@ur-solutions/react-hooks'
import { useCallback } from 'react'

// `WorkInfringementCategory.title` and `.description` are seeded in Norwegian and
// the backend can only render Norwegian or English itself. Identifiers (W1–W9) are
// stable, so the UI translates the same texts into every locale by identifier.
// `legalBasis` cites the Norwegian FATS regulation and stays untranslated.

const CATEGORY_TITLE_MESSAGES = {
  W1: 'infringements.work-category-title.w1',
  W2: 'infringements.work-category-title.w2',
  W3: 'infringements.work-category-title.w3',
  W4: 'infringements.work-category-title.w4',
  W5: 'infringements.work-category-title.w5',
  W7: 'infringements.work-category-title.w7',
  W8: 'infringements.work-category-title.w8',
  W9: 'infringements.work-category-title.w9',
} as const

const CATEGORY_DESCRIPTION_MESSAGES = {
  W1: 'infringements.work-category-description.w1',
  W2: 'infringements.work-category-description.w2',
  W3: 'infringements.work-category-description.w3',
  W4: 'infringements.work-category-description.w4',
  W5: 'infringements.work-category-description.w5',
  W7: 'infringements.work-category-description.w7',
  W8: 'infringements.work-category-description.w8',
  W9: 'infringements.work-category-description.w9',
} as const

type WorkCategoryIdentifier = keyof typeof CATEGORY_TITLE_MESSAGES

function asIdentifier(
  identifier: string | null | undefined
): WorkCategoryIdentifier | undefined {
  const key = identifier?.toUpperCase()

  return key && key in CATEGORY_TITLE_MESSAGES
    ? (key as WorkCategoryIdentifier)
    : undefined
}

/** The locale keys holding a category's texts, if this table knows the identifier. */
export function workInfringementCategoryKeys(
  identifier: string | null | undefined
) {
  const key = asIdentifier(identifier)

  return key
    ? {
        title: CATEGORY_TITLE_MESSAGES[key],
        description: CATEGORY_DESCRIPTION_MESSAGES[key],
      }
    : undefined
}

/**
 * Resolve work infringement category titles and descriptions in the viewer's
 * language, falling back to the texts the API returned for unknown identifiers.
 */
export function useWorkInfringementCategoryTranslations() {
  const titles = useTranslate(CATEGORY_TITLE_MESSAGES)
  const descriptions = useTranslate(CATEGORY_DESCRIPTION_MESSAGES)

  const title = useCallback(
    (identifier: string | null | undefined, apiTitle?: string | null) => {
      const key = asIdentifier(identifier)

      return (key && titles[key]) || apiTitle || ''
    },
    [titles]
  )

  const description = useCallback(
    (identifier: string | null | undefined, apiDescription?: string | null) => {
      const key = asIdentifier(identifier)

      return (key && descriptions[key]) || apiDescription || ''
    },
    [descriptions]
  )

  return { title, description }
}
